import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import * as LocalAuthentication from 'expo-local-authentication';
import { googleSignIn } from '../../utils/googleAuth';
import * as Apple from 'expo-apple-authentication';
import { Ionicons, FontAwesome } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from '../../context/AuthContext';

const API_BASE_URL = 'https://api.jobeng.ai';


export default function LearningLoginScreen({ navigation }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState({ email: false, password: false });
  const [isLoading, setIsLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState('');
  const [hasSavedCredentials, setHasSavedCredentials] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [appleAuthAvailable, setAppleAuthAvailable] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});



  useEffect(() => {
    checkBiometricAvailability();
    checkSavedCredentials();
    loadSavedData();
    checkAppleAuthAvailability();
  }, []);


  const loadSavedData = async () => {
    try {
      const savedEmail = await AsyncStorage.getItem('saved_learning_email');
      const rememberLogin = await AsyncStorage.getItem('remember_learning_login');
      
      if (savedEmail && rememberLogin === 'true') {
        setEmail(savedEmail);
        setRememberMe(true);
      }
    } catch (error) {
      console.error('Error loading saved data:', error);
    }
  };

  const checkBiometricAvailability = async () => {
    try {
      const hasHardware = await LocalAuthentication.hasHardwareAsync();
      const isEnrolled = await LocalAuthentication.isEnrolledAsync();
      const supportedTypes = await LocalAuthentication.supportedAuthenticationTypesAsync();
      
      const available = hasHardware && isEnrolled;
      setBiometricAvailable(available);
      
      if (available && supportedTypes.length > 0) {
        if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
          setBiometricType('Face ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
          setBiometricType('Touch ID');
        } else if (supportedTypes.includes(LocalAuthentication.AuthenticationType.IRIS)) {
          setBiometricType('Iris');
        } else {
          setBiometricType('Biometric');
        }
      }
      
      console.log('✅ Biometric availability checked:', { available, type: biometricType });
    } catch (error) {
      console.error('Error checking biometric availability:', error);
      setBiometricAvailable(false);
    }
  };

  // ✅ NEW: Check Apple Authentication Availability
  const checkAppleAuthAvailability = async () => {
    try {
      const isAvailable = await Apple.isAvailableAsync();
      setAppleAuthAvailable(isAvailable);
      console.log('✅ Apple Auth availability:', isAvailable);
    } catch (error) {
      console.error('Error checking Apple auth availability:', error);
      setAppleAuthAvailable(false);
    }
  };

  const checkSavedCredentials = async () => {
    try {
      const savedCredentials = await AsyncStorage.getItem('biometric_credentials_learning');
      setHasSavedCredentials(!!savedCredentials);
    } catch (error) {
      console.error('Error checking saved credentials:', error);
    }
  };

  // Input validation function
  const validateInputs = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!email.trim()) {
      errors.email = 'Please enter your email address';
    } else if (!emailRegex.test(email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!password.trim()) {
      errors.password = 'Please enter your password';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Improved error message function
  const getErrorMessage = (error, response) => {
    if (error.message.includes('Network request failed')) {
      return 'No internet connection. Please check your network.';
    }
    if (response?.status === 429) {
      return 'Too many attempts. Please try again later.';
    }
    if (response?.status === 500) {
      return 'Server error. Please try again later.';
    }
    if (response?.status === 503) {
      return 'Service temporarily unavailable. Please try again later.';
    }
    return 'Login failed. Please try again.';
  };

  const handleLogin = async () => {
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate inputs - if invalid, return immediately
    if (!validateInputs()) {
      return;
    }

    setIsLoading(true);
    
    try {
      console.log('Attempting learning login with data:', {
        email: email.trim().toLowerCase(),
        password: '***hidden***',
        user_type: 'learning'
      });

      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password: password.trim(),
          user_type: 'learning',
          remember_me: rememberMe
        }),
      });

      const responseText = await response.text();
      console.log('Login response status:', response.status);
      console.log('Raw login response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }

      console.log('Login response:', data);

      if (response.ok && data.success) {
        // Parse response data - FIXED: Handle the response structure correctly
        let userData = null;
        let token = null;

        if (data.data && data.data.user) {
          userData = data.data.user;
          token = data.data.token;
        } else if (data.user) {
          userData = data.user;
          token = data.token;
        }

        if (!userData) {
          throw new Error('Invalid response format: user data not found');
        }

        // Ensure user type is learning (check if server returned different type)
        if (userData.type === 'employee') {
          // Server returned employee type, but we need learning
          userData.user_type = 'learning';
          userData.type = 'learning';
        } else {
          userData.user_type = userData.type || 'learning';
        }

        if (token) {
          await AsyncStorage.setItem('auth_token', token);
          await AsyncStorage.setItem('user_data', JSON.stringify(userData));
        }

        // Save biometric credentials if biometric is available and remember me is enabled
        if (biometricAvailable && rememberMe && userData.email) {
          await saveBiometricCredentials(token, userData.id, userData.email);
        }

        // Save login preferences
        if (rememberMe && userData.email) {
          await AsyncStorage.setItem('saved_learning_email', userData.email);
          await AsyncStorage.setItem('remember_learning_login', 'true');
        } else {
          await AsyncStorage.removeItem('saved_learning_email');
          await AsyncStorage.removeItem('remember_learning_login');
        }

        if (login && token) {
          await login(userData, token);
        }

        console.log('✅ Learning login successful, navigating to: WelcomeLearningScreen');
        navigation.navigate('WelcomeLearningScreen');

      } else {
        let errorMessage = getErrorMessage({ message: '' }, response);
        
        if (data && data.error) {
          errorMessage = data.error;
        } else if (data && data.message) {
          errorMessage = data.message;
        } else if (response.status === 401) {
          errorMessage = 'Invalid email or password.';
        } else if (response.status === 422) {
          errorMessage = 'Please check your input data.';
        } else if (response.status === 403) {
          errorMessage = 'Account not verified. Please check your email.';
        }

        Alert.alert('Login Error', errorMessage);
      }

    } catch (error) {
      console.error('Learning login error:', error);
      
      let userMessage = getErrorMessage(error, null);
      
      if (error.message && !error.message.includes('Network request failed')) {
        userMessage = error.message;
      }

      Alert.alert('Login Error', userMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google Auth Success Handler
  const handleGoogleAuthSuccess = async (authResult) => {
    setIsLoading(true);
    try {
      console.log('🔐 Processing Google auth success for learning');

      if (!authResult || !authResult.accessToken) {
        throw new Error('Invalid Google authentication result');
      }

      // First try to get user info from Google
      let userInfo = null;
      try {
        const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${authResult.accessToken}`);
        userInfo = await userInfoResponse.json();
      } catch (userInfoError) {
        console.warn('Could not fetch Google user info:', userInfoError);
      }

      // Try server validation first
      try {
        const response = await fetch(`${API_BASE_URL}/api/auth/google-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            access_token: authResult.accessToken,
            id_token: authResult.idToken,
            user_info: userInfo,
            user_type: 'learning'
          }),
        });

        const result = await response.json();

        if (result.success) {
          // Store authentication data
          await AsyncStorage.setItem('auth_token', result.data.token);
          await AsyncStorage.setItem('user_data', JSON.stringify(result.data.user));

          // Save login preferences if remember me is enabled
          if (rememberMe && result.data.user.email) {
            await AsyncStorage.setItem('saved_learning_email', result.data.user.email);
            await AsyncStorage.setItem('remember_learning_login', 'true');
          }

          console.log('✅ Google login successful for learning');
          navigation.navigate('WelcomeLearningScreen');
          return; // Success, exit function
        }
      } catch (serverError) {
        console.warn('Server Google login failed, using fallback:', serverError);
      }

      // ✅ FALLBACK: If server validation fails, create local session
      if (userInfo && userInfo.email) {
        const fallbackUser = {
          id: userInfo.id || Date.now().toString(),
          email: userInfo.email,
          name: userInfo.name || 'Google Learning User',
          full_name: userInfo.name || 'Google Learning User',
          picture: userInfo.picture,
          user_type: 'learning',
          type: 'learning',
          loginMethod: 'google'
        };

        // Store fallback authentication data
        await AsyncStorage.setItem('auth_token', 'google_learning_fallback_' + Date.now());
        await AsyncStorage.setItem('user_data', JSON.stringify(fallbackUser));

        // Save login preferences
        if (rememberMe) {
          await AsyncStorage.setItem('saved_learning_email', fallbackUser.email);
          await AsyncStorage.setItem('remember_learning_login', 'true');
        }

        console.log('✅ Google fallback login successful for learning');
        navigation.navigate('WelcomeLearningScreen');
      } else {
        throw new Error('Unable to get user information from Google');
      }

    } catch (error) {
      console.error('❌ Google auth success error:', error);
      Alert.alert('Google Login Error', 'Unable to complete Google login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Google Login Handler
  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      console.log('🔐 Google login attempt for learning');


      // Native Google Sign-In (returns { accessToken, idToken })
      const authentication = await googleSignIn();
      await handleGoogleAuthSuccess(authentication);

    } catch (error) {
      if (error?.cancelled) return;
      console.error('❌ Google login error:', error);
      Alert.alert('Google Login Error', 'Unable to login with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ NEW: Apple Login Implementation
  const handleAppleLogin = async () => {
    if (!appleAuthAvailable) {
      Alert.alert('Apple Login', 'Apple Sign In is not available on this device');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🍎 Apple login attempt for learning');

      const credential = await Apple.signInAsync({
        requestedScopes: [
          Apple.AppleAuthenticationScope.FULL_NAME,
          Apple.AppleAuthenticationScope.EMAIL,
        ],
      });

      if (credential.identityToken) {
        console.log('🍎 Apple credential received:', {
          user: credential.user,
          email: credential.email,
          fullName: credential.fullName,
          identityToken: credential.identityToken ? 'present' : 'missing'
        });

        // Try server validation first
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/apple-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              identity_token: credential.identityToken,
              authorization_code: credential.authorizationCode,
              user_type: 'learning',
              email: credential.email,
              full_name: credential.fullName ? 
                `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
                null,
              user_info: {
                id: credential.user,
                email: credential.email,
                name: credential.fullName ? 
                  `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
                  'Apple Learning User',
                firstName: credential.fullName?.givenName,
                lastName: credential.fullName?.familyName
              }
            }),
          });

          const result = await response.json();

          if (result.success) {
            // Store authentication data
            await AsyncStorage.setItem('auth_token', result.data.token);
            await AsyncStorage.setItem('user_data', JSON.stringify(result.data.user));

            // Save login preferences if remember me is enabled
            if (rememberMe && result.data.user.email) {
              await AsyncStorage.setItem('saved_learning_email', result.data.user.email);
              await AsyncStorage.setItem('remember_learning_login', 'true');
            }

            console.log('✅ Apple server login successful for learning');
            navigation.navigate('WelcomeLearningScreen');
            return; // Success, exit function
          }
        } catch (serverError) {
          console.warn('Server Apple login failed, using fallback:', serverError);
        }

        // ✅ FALLBACK: If server validation fails, create local session
        const fallbackUser = {
          id: credential.user,
          email: credential.email || 'apple_learning_user@privaterelay.appleid.com',
          name: credential.fullName ? 
            `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
            'Apple Learning User',
          full_name: credential.fullName ? 
            `${credential.fullName.givenName || ''} ${credential.fullName.familyName || ''}`.trim() : 
            'Apple Learning User',
          first_name: credential.fullName?.givenName || 'Apple',
          last_name: credential.fullName?.familyName || 'User',
          user_type: 'learning',
          type: 'learning',
          loginMethod: 'apple',
        };

        // Store fallback authentication data
        await AsyncStorage.setItem('auth_token', 'apple_learning_' + Date.now());
        await AsyncStorage.setItem('user_data', JSON.stringify(fallbackUser));

        // Save login preferences
        if (rememberMe) {
          await AsyncStorage.setItem('saved_learning_email', fallbackUser.email);
          await AsyncStorage.setItem('remember_learning_login', 'true');
        }

        console.log('✅ Apple fallback login successful for learning');
        navigation.navigate('WelcomeLearningScreen');
      }
    } catch (error) {
      console.error('❌ Apple login error:', error);
      if (error.code !== 'ERR_CANCELED') {
        Alert.alert('Apple Login Error', 'Failed to complete Apple login. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ IMPROVED: Biometric Login
  const handleBiometricLogin = async () => {
    if (!biometricAvailable) {
      Alert.alert('Unavailable', 'Biometric authentication not available on this device');
      return;
    }

    if (!hasSavedCredentials) {
      Alert.alert('No Saved Credentials', 'Please login with email and password first and enable "Remember Me" to use biometric login');
      return;
    }

    try {
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: `Login with ${biometricType}`,
        cancelLabel: 'Cancel',
        fallbackLabel: 'Use Password',
        disableDeviceFallback: false,
      });

      if (result.success) {
        setIsLoading(true);
        
        // Check if we have saved biometric credentials
        const savedCredentials = await AsyncStorage.getItem('biometric_credentials_learning');
        if (!savedCredentials) {
          Alert.alert('Biometric Login', 'No saved biometric credentials found. Please login with email/password first.');
          return;
        }

        const credentials = JSON.parse(savedCredentials);
        
        // Try to validate biometric credentials with server
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/biometric-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              biometric_token: credentials.token,
              user_id: credentials.user_id,
              device_id: credentials.device_id || 'unknown',
              user_type: 'learning'
            }),
          });

          const result = await response.json();

          if (result.success) {
            // Store authentication data
            await AsyncStorage.setItem('auth_token', result.data.token);
            await AsyncStorage.setItem('user_data', JSON.stringify(result.data.user));

            console.log('✅ Biometric login successful for learning');
            navigation.navigate('WelcomeLearningScreen');
            return; // Success, exit function
          }
        } catch (serverError) {
          console.warn('Server biometric validation failed, using fallback:', serverError);
        }

        // ✅ FALLBACK: If server validation fails, use stored credentials
        if (credentials.email) {
          const fallbackUser = {
            id: credentials.user_id,
            email: credentials.email,
            name: credentials.name || 'Biometric Learning User',
            full_name: credentials.name || 'Biometric Learning User',
            user_type: 'learning',
            type: 'learning',
            loginMethod: 'biometric'
          };

          // Store fallback authentication data
          await AsyncStorage.setItem('auth_token', credentials.token);
          await AsyncStorage.setItem('user_data', JSON.stringify(fallbackUser));

          console.log('✅ Biometric fallback login successful for learning');
          navigation.navigate('WelcomeLearningScreen');
        } else {
          throw new Error('Invalid biometric credentials');
        }

      } else {
        if (result.error && result.error !== 'UserCancel') {
          Alert.alert('Authentication Failed', 'Biometric authentication not recognized');
        }
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      Alert.alert('Biometric Login Error', 'Please try again or use email and password');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ FIXED: Save biometric credentials - Fixed the TypeError
  const saveBiometricCredentials = async (userToken, userId, userEmail) => {
    try {
      if (biometricAvailable && rememberMe && userEmail && typeof userEmail === 'string') {
        const biometricCredentials = {
          token: userToken,
          user_id: userId,
          email: userEmail,
          name: 'Learning User',
          device_id: 'device_learning_' + Date.now(),
          created_at: new Date().toISOString()
        };

        await AsyncStorage.setItem('biometric_credentials_learning', JSON.stringify(biometricCredentials));
        setHasSavedCredentials(true);
        console.log('✅ Biometric credentials saved for learning');
      }
    } catch (error) {
      console.error('❌ Error saving biometric credentials:', error);
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        {/* === BACK BUTTON ADDED HERE === */}
        <TouchableOpacity 
          style={styles.backButton} 
          onPress={() => navigation.goBack()}
          accessibilityLabel="Go back"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back-outline" size={28} color="#007AFF" />
        </TouchableOpacity>
        {/* ============================== */}

        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.container}>
            <View style={styles.headerContainer}>
              <Image
                source={require('../../../assets/jobeng_academy_logo.png')}
                style={styles.logo}
                accessibilityLabel="JobEng Academy Logo"
                resizeMode="contain"
              />
              <Text style={styles.subtitle}>UK Engineering Excellence</Text>
            </View>

            <View style={styles.formContainer}>
              <Text style={styles.title} accessibilityRole="header">Learning Portal Login</Text>
              <Text style={styles.description}>
                Access your personalized learning journey
              </Text>

              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.email && styles.inputWrapperFocused,
                  validationErrors.email && styles.inputWrapperError
                ]}>
                  <Ionicons 
                    name="mail-outline" 
                    size={20} 
                    color={isFocused.email ? "#007AFF" : validationErrors.email ? "#FF3B30" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Student Email"
                    placeholderTextColor="#8E8E93"
                    value={email}
                    onChangeText={(text) => {
                      setEmail(text);
                      // Clear email error when user starts typing
                      if (validationErrors.email) {
                        setValidationErrors(prev => ({ ...prev, email: '' }));
                      }
                    }}
                    onFocus={() => setIsFocused(prev => ({ ...prev, email: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, email: false }))}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    accessibilityLabel="Email input"
                    accessibilityHint="Enter your student email address"
                    importantForAutofill="email"
                    textContentType="emailAddress"
                  />
                </View>
                {validationErrors.email && (
                  <Text style={styles.errorText}>{validationErrors.email}</Text>
                )}
              </View>

              <View style={styles.inputContainer}>
                <View style={[
                  styles.inputWrapper,
                  isFocused.password && styles.inputWrapperFocused,
                  validationErrors.password && styles.inputWrapperError
                ]}>
                  <Ionicons 
                    name="lock-closed-outline" 
                    size={20} 
                    color={isFocused.password ? "#007AFF" : validationErrors.password ? "#FF3B30" : "#8E8E93"} 
                    style={styles.inputIcon}
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Password"
                    placeholderTextColor="#8E8E93"
                    value={password}
                    onChangeText={(text) => {
                      setPassword(text);
                      // Clear password error when user starts typing
                      if (validationErrors.password) {
                        setValidationErrors(prev => ({ ...prev, password: '' }));
                      }
                    }}
                    onFocus={() => setIsFocused(prev => ({ ...prev, password: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, password: false }))}
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                    accessibilityLabel="Password input"
                    accessibilityHint="Enter your password"
                    importantForAutofill="password"
                    textContentType="password"
                  />
                  <TouchableOpacity 
                    onPress={() => setShowPassword(!showPassword)}
                    style={styles.eyeIcon}
                    accessibilityLabel={showPassword ? "Hide password" : "Show password"}
                    accessibilityRole="button"
                  >
                    <Ionicons 
                      name={showPassword ? "eye-off-outline" : "eye-outline"} 
                      size={20} 
                      color="#8E8E93" 
                    />
                  </TouchableOpacity>
                </View>
                {validationErrors.password && (
                  <Text style={styles.errorText}>{validationErrors.password}</Text>
                )}
              </View>

              {/* Remember Me & Forgot Password */}
              <View style={styles.rememberMeContainer}>
                <TouchableOpacity 
                  style={styles.rememberMeButton}
                  onPress={() => setRememberMe(!rememberMe)}
                  accessibilityLabel="Remember me"
                  accessibilityRole="checkbox"
                  accessibilityState={{checked: rememberMe}}
                >
                  <Ionicons 
                    name={rememberMe ? "checkbox" : "square-outline"} 
                    size={20} 
                    color="#007AFF" 
                  />
                  <Text style={styles.rememberMeText}>Remember Me</Text>
                </TouchableOpacity>

                <TouchableOpacity 
                  style={styles.forgotPasswordButton}
                  onPress={() => navigation.navigate('ForgotPassword', { userType: 'learning' })}
                  accessibilityLabel="Forgot password"
                  accessibilityRole="button"
                >
                  <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
                </TouchableOpacity>
              </View>

              <TouchableOpacity
                style={[styles.loginButton, isLoading && styles.loginButtonDisabled]}
                onPress={handleLogin}
                disabled={isLoading}
                accessibilityLabel="Login button"
                accessibilityRole="button"
                accessibilityState={{disabled: isLoading}}
              >
                {isLoading ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.loginButtonText}>Login to Learning Portal</Text>
                    <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
                  </>
                )}
              </TouchableOpacity>

              {/* Biometric Login */}
              {biometricAvailable && hasSavedCredentials && (
                <TouchableOpacity
                  style={styles.biometricButton}
                  onPress={handleBiometricLogin}
                  disabled={isLoading}
                  accessibilityLabel={`Login with ${biometricType}`}
                  accessibilityRole="button"
                  accessibilityState={{disabled: isLoading}}
                >
                  <Ionicons 
                    name={biometricType === 'Face ID' ? "scan" : "finger-print"} 
                    size={24} 
                    color="#007AFF" 
                  />
                  <Text style={styles.biometricButtonText}>
                    Login with {biometricType}
                  </Text>
                </TouchableOpacity>
              )}


              <View style={styles.footerContainer}>
                <Text style={styles.footerText}>
                  Don't have an account?{' '}
                  <Text 
                    style={styles.footerLink}
                    onPress={() => navigation.navigate('LearningSignup')}
                    accessibilityLabel="Sign up for an account"
                    accessibilityRole="link"
                  >
                    Sign Up
                  </Text>
                </Text>
                
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  /* === BACK BUTTON STYLE ADDED HERE === */
  backButton: {
    position: 'absolute',
    top: 10,
    left: 20,
    zIndex: 10,
    padding: 5,
  },
  /* ==================================== */
  keyboardAvoid: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 40,
  },
  headerContainer: {
    alignItems: 'center',
    marginBottom: 40,
    marginTop: 30,
  },
  logo: {
    width: 300,
    height: 220,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 16,
    color: '#8E8E93',
    fontWeight: '500',
  },
  formContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1C1C1E',
    textAlign: 'center',
    marginBottom: 8,
  },
  description: {
    fontSize: 16,
    color: '#8E8E93',
    textAlign: 'center',
    marginBottom: 32,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    paddingHorizontal: 16,
    height: 50,
  },
  inputWrapperFocused: {
    borderColor: '#007AFF',
    backgroundColor: '#FFFFFF',
  },
  inputWrapperError: {
    borderColor: '#FF3B30',
    backgroundColor: '#FFF5F5',
  },
  inputIcon: {
    marginRight: 12,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1C1C1E',
  },
  errorText: {
    color: '#FF3B30',
    fontSize: 12,
    marginTop: 4,
    marginLeft: 4,
  },
  eyeIcon: {
    marginLeft: 12,
    padding: 4,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  rememberMeButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rememberMeText: {
    marginLeft: 8,
    fontSize: 14,
    color: '#1C1C1E',
  },
  forgotPasswordButton: {
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  forgotPasswordText: {
    fontSize: 14,
    color: '#007AFF',
    fontWeight: '500',
  },
  loginButton: {
    backgroundColor: '#007AFF',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  loginButtonDisabled: {
    opacity: 0.7,
  },
  loginButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  biometricButton: {
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  biometricButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#007AFF',
    marginLeft: 8,
  },
  socialLoginContainer: {
    marginBottom: 24,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E5E5EA',
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: '#8E8E93',
  },
  socialButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  socialButton: {
    flex: 1,
    backgroundColor: '#F2F2F7',
    borderRadius: 12,
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 4,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  socialButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1C1C1E',
    marginLeft: 8,
  },
  footerContainer: {
    alignItems: 'center',
  },
  footerText: {
    fontSize: 14,
    color: '#8E8E93',
    marginBottom: 16,
  },
  footerLink: {
    color: '#007AFF',
    fontWeight: '500',
  },
  switchModeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  switchModeText: {
    fontSize: 14,
    color: '#007AFF',
    marginLeft: 4,
  },
});